// src/features/files/components/DeleteFolderDialog.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

import { foldersAPI } from '../services/folders';
import { getFolderName } from '../utils/getFolderName';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useUsageText } from './DeleteFilesDialog';

/**
 * Confirms deleting a folder with everything in it. It first asks the server what
 * that would remove. A folder holding files still used by equipment, tasks, safety
 * data sheets etc. can't be deleted (the server refuses too); those files are
 * listed with where they're used.
 */
export default function DeleteFolderDialog({ open, folder, onClose, onDeleted }) {
  const { t } = useTranslation(['files', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const usageText = useUsageText();
  const [usage, setUsage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !folder) return undefined;
    let cancelled = false;
    setUsage(null);
    setLoadError(null);
    foldersAPI.getFolderUsage(folder.folder_id)
      .then((res) => { if (!cancelled) setUsage(res.data?.data); })
      .catch((err) => { if (!cancelled) setLoadError(err.response?.data?.message || t('files:delete_folder.failed')); });
    return () => { cancelled = true; };
  }, [open, folder, t]);

  const name = folder ? getFolderName(folder.folder_name) : '';
  const isEmpty = usage && usage.file_count === 0 && usage.folder_count === 0;
  const canDelete = usage && !usage.in_use;

  // "2 undermapper og 14 filer"
  const contents = usage ? [
    usage.folder_count > 0 && t('files:delete_folder.folder_count', { count: usage.folder_count }),
    usage.file_count > 0 && t('files:delete_folder.file_count', { count: usage.file_count }),
  ].filter(Boolean).join(t('files:delete_folder.and')) : '';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await foldersAPI.deleteFolder(folder.folder_id, { recursive: !isEmpty });
      showSuccess(t('files:delete_folder.done', { name }));
      onDeleted?.(folder);
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('files:delete_folder.failed'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('files:delete_folder.title', { name })}</DialogTitle>
      <DialogContent>
        {loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : !usage ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : usage.in_use ? (
          <Alert severity="warning">
            {t('files:delete_folder.in_use', { count: usage.files_in_use.length })}
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {usage.files_in_use.map(file => (
                <ListItem key={file.file_id} disableGutters sx={{ py: 0 }}>
                  <ListItemText
                    primary={file.filename}
                    secondary={t('files:delete.used_by', { places: usageText(file.usages) })}
                    slotProps={{ secondary: { color: 'inherit' } }}
                  />
                </ListItem>
              ))}
            </List>
            {t('files:delete_folder.move_out', { count: usage.files_in_use.length })}
          </Alert>
        ) : (
          <>
            <DialogContentText>
              {isEmpty ? t('files:delete_folder.empty') : t('files:delete_folder.permanent', { contents })}
            </DialogContentText>
            {/* A shared folder can hold what other departments put there; that's theirs */}
            {usage.other_department_items > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('files:shared.delete_keeps_others', { count: usage.other_department_items })}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleting}>
          {canDelete || !usage ? t('common:buttons.cancel') : t('common:buttons.close')}
        </Button>
        {(canDelete || (!usage && !loadError)) && (
          <Button
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            onClick={handleDelete}
            disabled={!usage || deleting}
          >
            {isEmpty || !usage ? t('files:delete_folder.button') : t('files:delete_folder.button_all')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
